import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { black, gray } from '@avangard/ui/colors';

import { Hint, Tag } from '@modules/layout/atoms';
import { onlineApplicationStatusTranslates } from '@modules/students/constants';
import { getSummaryGroupLabel } from '@modules/students/helpers';
import { StudentOnlineApplicationBlock } from '@modules/students/moleculas';
import {
    StudentOnlineApplicationStep,
    StudentOnlineApplicationStatus,
} from '@modules/types/graphql';
import { StudentOnlineApplicationSteps } from './student-online-application-steps';

import type { UpdateStudentOnlineApplicationFormValues } from '@modules/students/requests';

const StudentOnlineApplicationHeader = () => {
    const { t } = useTranslation(['students']);

    const { values } = useFormikContext<UpdateStudentOnlineApplicationFormValues>();

    const status = React.useMemo(() => {
        if ([StudentOnlineApplicationStatus.correction].includes(values.currentStatus)) {
            return 'warning';
        }

        if ([StudentOnlineApplicationStatus.schoolApproved].includes(values.currentStatus)) {
            return 'info';
        }

        if ([StudentOnlineApplicationStatus.approved].includes(values.currentStatus)) {
            return 'success';
        }

        if ([StudentOnlineApplicationStatus.rejected].includes(values.currentStatus)) {
            return 'error';
        }

        return 'normal';
    }, [values.currentStatus]);

    const isIntroductionStep = values.currentStep === StudentOnlineApplicationStep.introduction;

    const description = React.useMemo(() => {
        if (isIntroductionStep) {
            return 'Ознакомьтесь с перечнем абсолютных противопоказаний к прохождению учебных сборов перед заполнением анкеты';
        }

        return 'Пожалуйста, заполните анкету и загрузите необходимые документы.';
    }, [isIntroductionStep]);

    const ageHint = getSummaryGroupLabel('ageHint', values.isAdult, values.isFutureAdult);

    return (
        <>
            <Deadline
                dangerouslySetInnerHTML={{
                    __html: values.originalStudentOnlineApplication.getDeadlineString(),
                }}
            />

            <StyledBlock>
                <Header>
                    <HeaderTagRow>
                        <Tag status={status}>
                            {t(onlineApplicationStatusTranslates[values.currentStatus])}
                        </Tag>
                    </HeaderTagRow>

                    <h2>
                        Здравия желаю, <br /> {values.summary.main.fullName}!
                    </h2>

                    <p>{description}</p>

                    {ageHint ? <StyledHint>{ageHint}</StyledHint> : null}
                </Header>

                {!isIntroductionStep ? (
                    <StudentOnlineApplicationSteps
                        allStepsPassed={values.originalStudentOnlineApplication.isFinalStatus()}
                        currentStep={values.currentStep}
                    />
                ) : null}
            </StyledBlock>
        </>
    );
};

const StyledBlock = styled(StudentOnlineApplicationBlock)`
    overflow: auto;
`;

const Deadline = styled.div`
    width: fit-content;
    margin: 0 auto 20px auto;
    padding: 8px 16px;

    color: ${black[80]};
    font-size: 1.4rem;
    font-weight: 400;

    background-color: ${gray[20]};

    span {
        font-weight: 600;
    }
`;

const Header = styled.div`
    > h2 {
        color: ${black[80]};
        font-size: 2.4rem;
        font-weight: 600;
    }

    > p {
        margin-top: 8px;

        color: ${black[54]};
        font-size: 1.4rem;
        font-weight: 600;
    }
`;

const HeaderTagRow = styled.div`
    width: 100%;
    margin-bottom: 16px;
`;

const StyledHint = styled(Hint)`
    margin-top: 24px;
`;

export { StudentOnlineApplicationHeader };
